import { collection, doc, getDoc, getDocs, setDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../../firebase';

/**
 * Colección de diseños PDF: servicios/{servicioId}/pdfDesigns
 * Cada diseño: { id, name, formId?, sections: [], headerFields?: [], logoUrl? (legacy), logoLeftUrl?, logoRightUrl?, primaryColor?, accentColor?, createdAt? }
 */
export const DEFAULT_HEADER_FIELDS = [
  { label: 'Vigilante', documentKey: 'vigilante' },
  { label: 'Servicio', documentKey: 'tienda' },
  { label: 'Cliente', documentKey: 'cliente' },
  { label: 'Fecha de subida', documentKey: 'fecha' }
];

const nameToId = (name) => {
  return String(name)
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '') || 'diseno_pdf';
};

/**
 * Obtiene todos los diseños PDF del servicio.
 * @param {string} servicioId - ID del servicio
 * @returns {Promise<Array>} - Lista de diseños
 */
export const getPdfDesigns = async (servicioId) => {
  if (!servicioId) return [];
  const ref = collection(db, 'servicios', servicioId, 'pdfDesigns');
  const snap = await getDocs(ref);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

/**
 * Obtiene un diseño PDF por ID.
 * @param {string} servicioId - ID del servicio
 * @param {string} designId - ID del diseño
 * @returns {Promise<Object|null>}
 */
export const getPdfDesignById = async (servicioId, designId) => {
  if (!servicioId || !designId) return null;
  const ref = doc(db, 'servicios', servicioId, 'pdfDesigns', designId);
  const snap = await getDoc(ref);
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

/**
 * Guarda un diseño PDF (crear o actualizar). Si no tiene id, se crea uno desde name.
 * @param {string} servicioId - ID del servicio
 * @param {Object} design - { name, sections: [ { title, fieldLabels: [] } ], id? }
 * @returns {Promise<Object>} - Diseño guardado con id
 */
export const savePdfDesign = async (servicioId, design) => {
  if (!servicioId || !design?.name) throw new Error('Servicio y nombre son obligatorios');
  const ref = collection(db, 'servicios', servicioId, 'pdfDesigns');
  const designId = design.id || nameToId(design.name);
  const docRef = doc(ref, designId);
  const payload = {
    name: String(design.name).trim(),
    sections: Array.isArray(design.sections) ? design.sections : [],
    updatedAt: new Date().toISOString(),
    // Siempre guardar estos campos con valores por defecto si no están definidos
    formId: design.formId || null,
    headerFields: Array.isArray(design.headerFields) ? design.headerFields : [],
    logoUrl: design.logoUrl || null,
    logoLeftUrl: design.logoLeftUrl || null,
    logoRightUrl: design.logoRightUrl || null,
    primaryColor: design.primaryColor || '#FFBF00',
    accentColor: design.accentColor || '#FFBF00'
  };
  if (!design.id) payload.createdAt = new Date().toISOString();
  await setDoc(docRef, payload);
  return { id: designId, ...payload };
};

/**
 * Elimina un diseño PDF.
 * @param {string} servicioId - ID del servicio
 * @param {string} designId - ID del diseño
 */
export const deletePdfDesign = async (servicioId, designId) => {
  if (!servicioId || !designId) return;
  const ref = doc(db, 'servicios', servicioId, 'pdfDesigns', designId);
  await deleteDoc(ref);
};

/**
 * Obtiene el diseño PDF aplicable a un documento (si el formulario del documento tiene pdfDesignId).
 * @param {string} servicioId - ID del servicio
 * @param {Object} document - Documento con documentTypeId (id del formulario)
 * @returns {Promise<Object|null>} - Diseño o null
 */
export const getPdfDesignForDocument = async (servicioId, document) => {
  console.log('🔍 [getPdfDesignForDocument] Iniciando:', { servicioId, documentTypeId: document?.documentTypeId });
  
  if (!servicioId || !document?.documentTypeId) {
    console.log('🔍 [getPdfDesignForDocument] Faltan parámetros, retornando null');
    return null;
  }
  
  const formRef = doc(db, 'servicios', servicioId, 'forms', document.documentTypeId);
  const formSnap = await getDoc(formRef);
  const formData = formSnap.exists() ? formSnap.data() : null;
  const pdfDesignId = formData?.pdfDesignId || null;
  
  console.log('🔍 [getPdfDesignForDocument] Formulario:', { 
    formExists: formSnap.exists(), 
    pdfDesignId,
    formName: formData?.name 
  });
  
  if (!pdfDesignId) {
    console.log('🔍 [getPdfDesignForDocument] Formulario sin pdfDesignId, retornando null');
    return null;
  }
  
  const design = await getPdfDesignById(servicioId, pdfDesignId);
  console.log('🔍 [getPdfDesignForDocument] Diseño obtenido:', { 
    designExists: !!design, 
    primaryColor: design?.primaryColor,
    accentColor: design?.accentColor,
    headerFields: design?.headerFields?.length || 0
  });
  
  return design;
};
